// Helpers for mirroring the sidebar tree cache (open/closed, search, sort,
// per-entry expansions, visible counts) into the ?ui= URL param. App.jsx
// owns the in-memory cache and the debounced write; these are the pure
// transforms it applies before serialisation, kept here so they're testable
// without standing up the whole component.
//
// Cache keys hang every dim pane off the scope path it lives under:
//   `scope:${encodeScopePath(parentScopePath)}:${depth}:${dimKey}`

// Recover the encoded scope-path prefix from a cache key so we can tell
// whether an entry still belongs to something the user has open. depth is
// numeric and dimKey has no colons, so the path is everything before the
// last two `:` fields. Returns null for non-scope (or unparseable) keys,
// which the caller leaves untouched.
export function scopePathOfCacheKey(key) {
  if (typeof key !== "string" || !key.startsWith("scope:")) return null;
  const body = key.slice("scope:".length);
  const dimColon = body.lastIndexOf(":");
  if (dimColon < 0) return null;
  const depthColon = body.lastIndexOf(":", dimColon - 1);
  if (depthColon < 0) return null;
  return body.slice(0, depthColon);
}

// The encoded scope-paths a tab/pin at `steps` keeps alive: the path itself
// and every ancestor prefix (ScopeColumns renders a column at each), plus
// root. `encode` is encodeScopePath, injected so this stays dependency-free.
// Anything not in the returned set is orphan residue from a tab that's since
// been re-navigated or closed.
export function liveScopePathsFor(stepArrays, encode) {
  const live = new Set([""]);
  for (const steps of stepArrays) {
    for (let i = 1; i <= steps.length; i++) live.add(encode(steps.slice(0, i)));
  }
  return live;
}

// Strip default / empty values so the serialized cache stays compact in the
// URL. Mirrors the defaults that DimensionPane / PaginatedExplorer initialize
// with — anything matching default doesn't need persisting. When
// `liveScopePaths` is given, also drop orphan sidebar entries whose scope-path
// no open tab/pin references; this only shrinks the serialized output — pass
// the in-memory cache unfiltered and it's left intact.
export function pruneCacheForUrl(cache, liveScopePaths) {
  const out = {};
  if (!cache || typeof cache !== "object") return out;
  for (const key of Object.keys(cache)) {
    if (liveScopePaths) {
      const sp = scopePathOfCacheKey(key);
      if (sp !== null && !liveScopePaths.has(sp)) continue;
    }
    const val = cache[key];
    if (!val || typeof val !== "object") continue;
    const cleaned = {};
    if (val.open === true || val.open === false) cleaned.open = val.open;
    if (typeof val.search === "string" && val.search !== "") cleaned.search = val.search;
    if (typeof val.sort === "string" && val.sort && val.sort !== "activity") cleaned.sort = val.sort;
    if (val.entryExp && typeof val.entryExp === "object") {
      const exp = {};
      for (const k of Object.keys(val.entryExp)) {
        if (val.entryExp[k]) exp[k] = true;
      }
      if (Object.keys(exp).length) cleaned.entryExp = exp;
    }
    if (typeof val.visible === "number" && val.visible > 0) cleaned.visible = val.visible;
    if (Object.keys(cleaned).length) out[key] = cleaned;
  }
  return out;
}
